import React from "react";

import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import MultipleSelector from "../../components/ui/MultipleSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

const HazardDetails = ({ project, hazard, setHazard }) => {
  const handleChange = (name, value) => {
    setHazard((prev) => {
      return { ...prev, [name]: value };
    });
  };

  return (
    <>
      {project && hazard && (
        <div className="grid grid-cols-2 gap-4 space-y-2">
          {/* First Row */}
          <div>
            <Label>Risk Status</Label>

            <Select
              value={hazard.riskStatus}
              onValueChange={(value) => handleChange("riskStatus", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select risk status" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {[
                  "Open",
                  "Managed",
                  "Resolved",
                  "For Transfer",
                  "Transferred",
                  "Closed",
                  "Cancelled",
                ].map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Empty column for alignment */}
          <div />

          {/* Second Row */}
          <div>
            <Label>ID</Label>

            <Input
              value={hazard.id ? `${hazard.hazardId}` : "Automatic ID"}
              disabled
            />
          </div>

          <div>
            <Label>Risk Category</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={[
                { label: "None", value: "None" },
                { label: "Environmental", value: "Environmental" },
                { label: "Performance", value: "Performance" },
                { label: "Safety", value: "Safety" },
              ]}
              value={
                hazard.riskCategory.map((category) => {
                  return { label: category, value: category };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "riskCategory",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select risk categories..."
            />
          </div>

          {/* Third Row */}
          <div>
            <Label>Hazard Type</Label>

            <Select
              value={hazard.type}
              onValueChange={(value) => handleChange("type", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Hazard Type" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {[
                  "Mechanical",
                  "Electrical",
                  "Chemical",
                  "Geological",
                  "Biological",
                  "Radiation",
                  "Human-made",
                  "Environmental",
                  "Thermal",
                ].map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Hazard Source</Label>

            <Input
              value={hazard.hazardSource}
              onChange={(e) => handleChange("hazardSource", e.target.value)}
              placeholder="Example: Exposed Wires, Equipment Failure, etc."
            />
          </div>

          {/* Fourth Row */}
          <div className="col-span-2">
            <Label>Description</Label>

            <Textarea
              value={hazard.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Example: Exposed wires in maintenance access panel..."
              className="min-h-[80px] resize-none"
            />
          </div>

          <div>
            <Label>Meeting</Label>

            <Select
              value={hazard.meetings}
              onValueChange={(value) => handleChange("meetings", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Meeting" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {project.projectMetadata?.meetings?.map((meeting) => (
                  <SelectItem key={meeting.number} value={meeting.number}>
                    {meeting.number} -{" "}
                    {new Date(meeting.date).toLocaleDateString("en-GB")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fifth Row */}
          <div>
            <Label>System(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.systems?.map((system) => {
                  return { label: system.name, value: system.name };
                }) || []
              }
              value={
                hazard.systems.map((system) => {
                  return { label: system, value: system };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "systems",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select systems..."
            />
          </div>

          <div>
            <Label>Operating Mode(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.operatingModes.map((operatingMode) => {
                  return {
                    label: operatingMode,
                    value: operatingMode,
                  };
                }) || []
              }
              value={
                hazard.operatingModes.map((operatingMode) => {
                  return { label: operatingMode, value: operatingMode };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "operatingModes",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select operating modes..."
            />
          </div>

          {/* Sixth Row */}
          <div>
            <Label>Location(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.locations.map((location) => {
                  return { label: location, value: location };
                }) || []
              }
              value={
                hazard.locations.map((location) => {
                  return { label: location, value: location };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "locations",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select location(s)..."
            />
          </div>

          <div>
            <Label>Project Lifecycle(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.projectLifecycles.map(
                  (projectLifecycle) => {
                    return {
                      label: projectLifecycle,
                      value: projectLifecycle,
                    };
                  }
                ) || []
              }
              value={
                hazard.projectLifecycles.map((projectLifecycle) => {
                  return {
                    label: projectLifecycle,
                    value: projectLifecycle,
                  };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "projectLifecycles",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select project lifecycle(s)..."
            />
          </div>

          {/* Seventh Row */}
          <div>
            <Label>Discipline(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.disciplines.map((discipline) => {
                  return { label: discipline, value: discipline };
                }) || []
              }
              value={
                hazard.disciplines.map((discipline) => {
                  return { label: discipline, value: discipline };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "disciplines",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select discipline(s)..."
            />
          </div>

          <div>
            <Label>Drawing Package(s)</Label>

            <MultipleSelector
              hidePlaceholderWhenSelected
              defaultOptions={
                project.projectMetadata?.drawingPackages.map(
                  (drawingPackage) => {
                    return {
                      label: drawingPackage,
                      value: drawingPackage,
                    };
                  }
                ) || []
              }
              value={
                hazard.drawingPackages.map((drawingPackage) => {
                  return {
                    label: drawingPackage,
                    value: drawingPackage,
                  };
                }) || []
              }
              onChange={(value) =>
                handleChange(
                  "drawingPackages",
                  value.map((x) => x.value)
                )
              }
              placeholder="Select drawing package(s)..."
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HazardDetails;
